import { useCallback, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { AppConfig } from "../AppConfig";
import { ModalSubTitleWithBorder } from "../components/organisms/modal/PopupModal.styles";
import {
  EarlyBirdPageRoutesToCheck,
  InitialPages,
  PageRoutes,
} from "../constants/routes";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions } from "../ducks/app";
import { Campaign, campaignActions } from "../ducks/campaign";
import { AppCoupon, couponActions } from "../ducks/coupon";
import { MenuInCart } from "../ducks/menu";
import { sortOrderActions } from "../ducks/sortOrder";
import { store } from "../store";
import { useNavigation } from "./useNavigation";

export const useEarlyBird = () => {
  const location = useLocation();
  const { navigateToPage } = useNavigation();

  const menusInCart = LocalStorageManager.Instance.getObject<MenuInCart[]>(
    AppConfig.Instance.LocalStorageKey["menusInCart"],
  );

  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  const clearEarlyBirdData = useCallback(() => {
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
    );
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["campaign"],
    );
  }, []);

  const isDirectAccess = useMemo(() => {
    // Legacy Navigation API
    if (performance.navigation && performance.navigation.type === 1)
      return true;
    // Modern Navigation Timing API
    if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType("navigation");
      if (navEntries.length > 0) {
        const navEntry = navEntries[0] as PerformanceNavigationTiming;
        if (navEntry.type === "reload") return true;
      }
    }
    // Direct access
    if (!document.referrer || document.referrer === "") return true;
    return false;
  }, []);

  const isInPageToShowModal = useMemo(
    () =>
      EarlyBirdPageRoutesToCheck.some((pattern) =>
        matchPath(pattern, location.pathname),
      ),
    [location.pathname],
  );

  const handleWhenHasEarlyBirdData = useCallback(() => {
    checkCampaign({
      fetchOnly: true,
      onNoCampaign: () => {
        displayExitModal();
      },
      onHasCampaign: () => {
        // 早割・在庫販売キャンペーンをLocalStorageに保存
        const earlyBirdCampaign = store
          .getState()
          .campaign.activeCampaigns.find((c) => c.type === "early_bird");
        const stockItemCampaign = store
          .getState()
          .campaign.activeCampaigns.find((c) => c.type === "stock_item");
        // 現在選択中のキャンペーン種別を優先して保存
        const activeCampaign =
          campaign?.type === "stock_item"
            ? stockItemCampaign || earlyBirdCampaign
            : earlyBirdCampaign || stockItemCampaign;
        LocalStorageManager.Instance.saveObject(
          AppConfig.Instance.LocalStorageKey["campaign"],
          activeCampaign,
        );
        displayConfirmModal();
      },
    });
  }, [campaign]);

  const displayExitModal = useCallback(() => {
    store.dispatch(
      appActions.showModal.started({
        title: "早割キャンペーンは\n終了しています。",
        subTitle: (
          <ModalSubTitleWithBorder>
            <p>
              通常の注文に戻ると
              <br />
              カートのメニューが削除されます。
            </p>
          </ModalSubTitleWithBorder>
        ),
        nextButtonLabel: "通常の注文に戻る",
        handleNext: () => {
          store.dispatch(appActions.closeModal());
          clearEarlyBirdData();
          navigateToPage(PageRoutes.HOME);
        },
      }),
    );
  }, [clearEarlyBirdData, navigateToPage]);

  const displayConfirmModal = useCallback(() => {
    store.dispatch(
      appActions.showModal.started({
        title:
          campaign?.type === "stock_item"
            ? "数量限定注文が途中です\nこのまま続けますか？"
            : "早割の注文が途中です\nこのまま続けますか？",
        subTitle: (
          <ModalSubTitleWithBorder>
            <p>
              通常の注文に戻ると
              <br />
              カートのメニューが削除されます。
            </p>
          </ModalSubTitleWithBorder>
        ),
        nextButtonLabel: "はい",
        closeButtonLabel: "通常の注文に戻る",
        handleClose: () => {
          store.dispatch(appActions.closeModal());
          clearEarlyBirdData();
          const state = store.getState();
          const selectedAppCoupons = state.coupon.selectedAppCoupons;
          const unselectedAppCoupons = state.coupon.unselectedAppCoupons;
          const continueRegularOrder =
            (selectedAppCoupons && selectedAppCoupons.length > 0) ||
            (unselectedAppCoupons && unselectedAppCoupons.length > 0);
          if (continueRegularOrder) {
            navigateToPage(PageRoutes.HOME, true, { isRegular: true });
          } else {
            navigateToPage(PageRoutes.CAMPAIGN_SELECTION);
          }
        },
        handleNext: () => {
          store.dispatch(appActions.closeModal());
          const state = store.getState();
          const selectedAppCoupons = state.coupon.selectedAppCoupons;
          const unselectedAppCoupons = state.coupon.unselectedAppCoupons;
          const hasAppLinkageCoupons =
            (selectedAppCoupons && selectedAppCoupons.length > 0) ||
            (unselectedAppCoupons && unselectedAppCoupons.length > 0);
          if (hasAppLinkageCoupons) {
            const keysToRemove = [
              "menusInCart",
              "appliedCoupons",
              "selectedAppCoupons",
              "unselectedAppCoupons",
              "appToken",
            ];
            keysToRemove.forEach((key) => {
              LocalStorageManager.Instance.removeObject(
                AppConfig.Instance.LocalStorageKey[key],
              );
            });
            store.dispatch(couponActions.resetToInitialState());
          }
        },
      }),
    );
  }, [clearEarlyBirdData, navigateToPage]);

  const checkCampaign = useCallback(
    (params?: {
      fetchOnly?: boolean;
      onNoCampaign?: () => void;
      onHasCampaign?: () => void;
    }) => {
      const { fetchOnly, onNoCampaign, onHasCampaign } = params ?? {};
      store.dispatch(appActions.showLoading());

      if (fetchOnly) {
        // fetchOnly is true: Only check for campaign without waiting for sort order
        store.dispatch(
          campaignActions.fetchActiveCampaign.started({
            onSuccess: (data) => {
              const hasEarlyBird = data.campaigns?.some((c) => c.type === 1);
              const hasStockItem = data.campaigns?.some((c) => c.type === 2);
              const hasCampaign = hasEarlyBird || hasStockItem;
              if (hasCampaign) onHasCampaign?.();
              else onNoCampaign?.();
            },
            onComplete: () => store.dispatch(appActions.hideLoading()),
            onError: () => {
              onNoCampaign?.();
              store.dispatch(appActions.hideLoading());
            },
          }),
        );
        return;
      }

      let hasCampaign = false;
      let campaignDone = false;
      let sortOrderDone = false;

      const tryNavigate = () => {
        if (!campaignDone || !sortOrderDone) return;
        store.dispatch(appActions.hideLoading());
        if (!hasCampaign) return;
        const isInitialPage = InitialPages.some((page) =>
          matchPath(page, location.pathname),
        );
        const encryptedCoupons = new URLSearchParams(location.search)?.get("q");
        const selectedAppCoupons = LocalStorageManager.Instance.getObject<
          AppCoupon[]
        >(AppConfig.Instance.LocalStorageKey["selectedAppCoupons"]);
        const unselectedAppCoupons = LocalStorageManager.Instance.getObject<
          string[]
        >(AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"]);
        if (
          isInitialPage &&
          !location?.state?.isRegular &&
          (!selectedAppCoupons || selectedAppCoupons.length === 0) &&
          (!unselectedAppCoupons || unselectedAppCoupons.length === 0) &&
          !encryptedCoupons
        )
          navigateToPage(PageRoutes.CAMPAIGN_SELECTION);
      };

      store.dispatch(
        sortOrderActions.getSortOrder.started({
          onSuccess: () => {
            sortOrderDone = true;
            tryNavigate();
          },
          onError: () => {
            sortOrderDone = true;
            tryNavigate();
          },
        }),
      );

      store.dispatch(
        campaignActions.fetchActiveCampaign.started({
          onSuccess: (data) => {
            const hasEarlyBird = data.campaigns?.some((c) => c.type === 1);
            const hasStockItem = data.campaigns?.some((c) => c.type === 2);
            hasCampaign = hasEarlyBird || hasStockItem;
            if (hasCampaign) onHasCampaign?.();
            else onNoCampaign?.();
            campaignDone = true;
            tryNavigate();
          },
          onComplete: () => {},
          onError: () => {
            onNoCampaign?.();
            campaignDone = true;
            tryNavigate();
          },
        }),
      );
    },
    [location.pathname, location.state, navigateToPage],
  );

  const checkCampaignStatus = useCallback(
    ({ onOk }: { onOk?: () => void }) => {
      store.dispatch(appActions.closeModal());
      const menusEarlyBirdInCart = menusInCart?.some(
        (menu) => menu.isEarlyBird,
      );
      const hasEarlyBirdData = !!campaign || !!menusEarlyBirdInCart;
      if (isInPageToShowModal && hasEarlyBirdData)
        return checkCampaign({
          fetchOnly: true,
          onNoCampaign: () => {
            displayExitModal();
          },
          onHasCampaign: () => {
            // 早割・在庫販売キャンペーンをLocalStorageに保存
            const earlyBirdCampaign = store
              .getState()
              .campaign.activeCampaigns.find((c) => c.type === "early_bird");
            const stockItemCampaign = store
              .getState()
              .campaign.activeCampaigns.find((c) => c.type === "stock_item");
            // 現在選択中のキャンペーン種別を優先して保存
            const activeCampaign =
              campaign?.type === "stock_item"
                ? stockItemCampaign || earlyBirdCampaign
                : earlyBirdCampaign || stockItemCampaign;
            LocalStorageManager.Instance.saveObject(
              AppConfig.Instance.LocalStorageKey["campaign"],
              activeCampaign,
            );
            onOk?.();
          },
        });
      onOk?.();
    },
    [campaign, menusInCart, isInPageToShowModal, displayExitModal],
  );

  return {
    isDirectAccess,
    isInPageToShowModal,
    menusInCart,
    campaign,
    checkCampaign,
    displayExitModal,
    displayConfirmModal,
    handleWhenHasEarlyBirdData,
    checkCampaignStatus,
  };
};
